import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { API } from '../config';
import { useBgReport, useBgJob } from './BackgroundJobs';
import AddressCascade from './AddressCascade';
import staffTopoFetcher from './staffTopoFetcher';

// Verifica Copertura TIM (sito TIM, fibra/ADSL) per indirizzo — chiamata sincrona /staff-ai/copertura-tim.
const IconClose = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
);

const EMPTY = { comune: '', via: '', civico: '' };
const esitoColor = (e) => {
  const s = (e || '').toUpperCase();
  if (s.includes('NON CENS')) return '#9ca3af';
  if (s.includes('NON')) return '#e11d48';
  if (s.includes('COPERT') || s.includes('OK') || s.includes('DISPONIB')) return '#22c55e';
  return '#3b82f6';
};
// Mbps -> etichetta leggibile (>=1000 in Gbit/s, es. 10000 -> "10 Gbit/s", 2500 -> "2,5 Gbit/s").
const fmtVel = (mbps) => {
  if (mbps == null || mbps === '') return null;
  const n = Number(mbps);
  if (!isFinite(n)) return String(mbps);
  return n >= 1000 ? `${(n / 1000).toLocaleString('it-IT')} Gbit/s` : `${n.toLocaleString('it-IT')} Mbps`;
};
const Riga = ({ l, children }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
    <span style={{ color: 'rgba(255,255,255,.5)' }}>{l}</span>
    <span style={{ color: '#fff', fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>{children}</span>
  </div>
);

export default function CoperturaTimDialog({ open, onClose }) {
  const [addr, setAddr] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const clear = () => { setAddr(EMPTY); setBusy(false); setRes(null); };

  const avvia = async () => {
    if (!addr.comune.trim() || !addr.via.trim()) return;
    setBusy(true); setRes(null);
    try { const r = await axios.post(`${API}/staff-ai/copertura-tim`, { comune: addr.comune, via: addr.via, civico: addr.civico }); setRes(r.data); }
    catch (e) { setRes({ errore: e.response?.data?.detail || 'Errore verifica' }); }
    setBusy(false);
  };

  const errore = !!res?.errore;
  const summary = res ? (errore ? (res.errore || 'errore') : `${res.esito || '—'}${res.tecnologia ? ' · ' + res.tecnologia : ''}`) : null;
  useBgReport('coptim', { label: 'Copertura TIM', color: '#3b82f6', iconKey: 'coptim' },
    { open, running: busy, done: !busy && !!res && !errore, error: errore, summary, active: busy || !!res });

  const entry = useBgJob('coptim');
  const had = useRef(false);
  useEffect(() => { if (entry) had.current = true; else if (had.current) { had.current = false; clear(); } }, [entry]); // eslint-disable-line

  if (!open) return null;

  return createPortal(
    <div className="bulk-overlay" onClick={onClose}>
      <div className="bulk-dialog" onClick={(e) => e.stopPropagation()} style={{ width: 600 }}>
        <div className="bulk-content">
          <div className="bulk-head" style={{ marginBottom: 14 }}>
            <span className="bulk-title">Copertura TIM · sito</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {busy && (
                <button onClick={onClose} title="Metti in background: la verifica continua, riapri dal pulsante in basso a destra"
                  style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,.22)', background: 'rgba(255,255,255,.06)', color: '#fff', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>⤓ Background</button>
              )}
              <button className="bulk-x" onClick={onClose}><IconClose /></button>
            </div>
          </div>

          <div style={{ marginBottom: 12, opacity: busy ? 0.55 : 1, pointerEvents: busy ? 'none' : 'auto' }}>
            <AddressCascade variant="desktop" inputClass="topo-in" fetcher={staffTopoFetcher} onChange={setAddr} rowLayout />
          </div>
          <button onClick={avvia} disabled={busy || !addr.comune.trim() || !addr.via.trim()}
            style={{ width: '100%', padding: '10px 16px', borderRadius: 9, border: 0, fontWeight: 800, fontSize: 13, cursor: busy ? 'default' : 'pointer', opacity: busy || !addr.comune.trim() || !addr.via.trim() ? 0.6 : 1, color: '#fff', background: 'linear-gradient(180deg,#60a5fa,#2563eb)' }}>
            {busy ? 'Verifica in corso…' : 'Verifica copertura'}
          </button>

          {busy ? (
            <div style={{ padding: '12px 0 4px' }}>
              <div className="indaga-track"><div className="indaga-fill" style={{ width: '100%', background: 'linear-gradient(90deg,#2563eb,#60a5fa,#2563eb)' }} /></div>
            </div>
          ) : res ? (
            errore ? <div style={{ color: '#fca5a5', fontSize: 13, marginTop: 12 }}>{res.errore}</div> : (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 800, color: '#fff', background: esitoColor(res.esito), padding: '6px 14px', borderRadius: 10, marginBottom: 10 }}>
                  {res.esito || '—'}{res.tecnologia ? ` · ${res.tecnologia}` : ''}
                </div>
                <div style={{ display: 'grid', gap: 8, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 10, padding: '11px 13px', fontSize: 13 }}>
                  {res.raw && <Riga l="Verdetto TIM">{res.raw}</Riga>}
                  {res.tecnologia && <Riga l="Tecnologia">{res.tecnologia}</Riga>}
                  <Riga l="Download">{fmtVel(res.down) || '—'}</Riga>
                  <Riga l="Upload">{fmtVel(res.up) || '—'}</Riga>
                  <Riga l="Latenza">{res.latenza != null && res.latenza !== '' ? `${res.latenza} ms` : '—'}</Riga>
                  {res.indirizzo_norm && <Riga l="Indirizzo">{res.indirizzo_norm}</Riga>}
                </div>
                {res.tecnologia === 'FWA' && res.down == null && res.up == null && (
                  <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.5)', marginTop: 7 }}>
                    Per l'FWA il sito TIM non pubblica le velocità (mostra solo la latenza).
                  </div>
                )}
              </div>
            )
          ) : entry && entry.status !== 'running' ? (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,.8)', padding: '12px 0 0' }}>Ultimo esito: <b style={{ color: '#fff' }}>{entry.summary || '—'}</b> <span style={{ color: 'rgba(255,255,255,.45)' }}>(fai una nuova verifica sopra)</span></div>
          ) : (
            <div style={{ color: 'rgba(255,255,255,.55)', fontSize: 13, padding: '12px 0 0' }}>
              Inserisci l'indirizzo: interrogo il sito TIM e mostro copertura, tecnologia (FTTH/FTTC/ADSL) e velocità.
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
